import { useState } from "react";
import { Link } from "react-router-dom";
import { galleryImages, galleryCategories } from "@/mocks/gallery";

export default function GalleryPreview() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightbox, setLightbox] = useState<null | { image: string; title: string; category: string }>(null);

  const filtered = activeCategory === "All"
    ? galleryImages.slice(0, 9)
    : galleryImages.filter((g) => g.category === activeCategory).slice(0, 9);

  const heightMap: Record<string, string> = {
    tall: "h-72",
    medium: "h-56",
    short: "h-44",
  };

  return (
    <section id="gallery-preview" className="bg-brand-beige py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-display text-brand-gold text-xs tracking-[4px] uppercase block mb-4">Visual Showcase</span>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-darkbrown font-bold">
            Our <em>Gallery</em>
          </h2>
          <p className="font-sans text-brand-muted mt-4 max-w-xl mx-auto text-sm leading-relaxed">
            A curated collection of our finest handcrafted pieces, captured in beautiful homes and spaces.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {galleryCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap font-display text-xs tracking-widest uppercase px-5 py-2 rounded-full border transition-all duration-300 cursor-pointer ${
                activeCategory === cat
                  ? "bg-brand-brown border-brand-brown text-brand-cream"
                  : "border-brand-brown/30 text-brand-brown hover:border-brand-gold hover:text-brand-gold"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="break-inside-avoid group relative overflow-hidden rounded-lg cursor-pointer"
              onClick={() => setLightbox({ image: item.image, title: item.title, category: item.category })}
            >
              <div className={`w-full ${heightMap[item.height] || "h-56"} overflow-hidden`}>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-brand-darkbrown/0 group-hover:bg-brand-darkbrown/50 transition-all duration-400 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 border-2 border-white rounded-full">
                    <i className="ri-zoom-in-line text-white text-lg" />
                  </div>
                  <p className="font-sans text-white text-sm font-500">{item.title}</p>
                </div>
              </div>
              {/* Category Tag */}
              <div className="absolute top-3 right-3 bg-brand-gold text-brand-darkbrown font-display text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {item.category}
              </div>
            </div>
          ))}
        </div>

        {/* View All */}
        <div className="text-center mt-12">
          <Link
            to="/gallery"
            className="whitespace-nowrap inline-block font-display text-sm tracking-widest uppercase px-12 py-4 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer"
          >
            View Full Gallery
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-brand-darkbrown/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-10 right-0 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
            <div className="w-full h-[60vh] rounded-lg overflow-hidden">
              <img
                src={lightbox.image}
                alt={lightbox.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="font-serif text-xl text-brand-cream">{lightbox.title}</p>
              <p className="font-display text-xs text-brand-gold tracking-widest uppercase mt-1">{lightbox.category}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
